import { TranslateService } from '@ngx-translate/core';
import { Injectable, OnDestroy } from '@angular/core';
import { QueryBuilderClassNames } from './query-builder.interfaces';
import { remove, forEach } from 'lodash';
import { SettingService } from 'src/app/core/service/setting.service';
import { Subscription } from 'rxjs';
import { MessengerService } from 'src/app/core/service/messenger.service';

@Injectable({
  providedIn: 'root'
})
export class QueryBuilderService implements OnDestroy
{
  public mainSections: Array<{ text: string; value: string; hidden: boolean; icon: string }> = [
    { text: 'Event', value: 'Event', hidden: true, icon: 'fa-calendar' },
    { text: 'Patient', value: 'Patient', hidden: true, icon: 'fa-user' },
    { text: 'Clinical History', value: 'ClinicalHistory', hidden: true, icon: '' }
  ];

  public subSections: Array<{ text: string; value: string; hidden: boolean; icon: string; iconLeftMargin: string; selected: boolean }> = [
    { text: 'Allergies', value: 'Allergies', hidden: true, icon: 'fa-allergies', iconLeftMargin: '', selected: false },
    { text: 'Care Plans', value: 'CarePlans', hidden: true, icon: 'fa-clipboard-list', iconLeftMargin: '', selected: false },
    { text: 'Care Team Members', value: 'CareTeamMembers', hidden: true, icon: 'fa-users', iconLeftMargin: '-4px', selected: false },
    { text: 'Encounters', value: 'Encounters', hidden: true, icon: 'fa-address-book', iconLeftMargin: '', selected: false },
    { text: 'Immunizations', value: 'Immunizations', hidden: true, icon: 'fa-syringe', iconLeftMargin: '', selected: false },
    { text: 'Lab Orders/Results', value: 'LabOrders', hidden: true, icon: 'fa-flask', iconLeftMargin: '', selected: false },
    { text: 'Medications', value: 'Medications', hidden: true, icon: 'fa-pills', iconLeftMargin: '', selected: false },
    { text: 'Problems', value: 'Problems', hidden: true, icon: 'fa-frown', iconLeftMargin: '', selected: false },
    { text: 'Procedures', value: 'Procedures', hidden: true, icon: 'fa-procedures', iconLeftMargin: '-4px', selected: false },
    { text: 'Vitals', value: 'Vitals', hidden: true, icon: 'fa-heartbeat', iconLeftMargin: '', selected: false },
    { text: 'Suicide Assessment', value: 'SuicideAssessment', hidden: true, icon: 'fa-stethoscope', iconLeftMargin: '', selected: false },
    {
      text: 'Mental Health Problems', value: 'MentalHealthProblems', hidden: true, icon: 'fa-medkit', iconLeftMargin: '-1px',
      selected: false
    }
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private translate: TranslateService,
    private settingService: SettingService,
    private messengerService: MessengerService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Transform the field configuration object to Query Builder configuration. This create a JSON string and returns the object.
   *
   * @param source field configuration source object to transform to Query Builder fields.
   */
  public transform(source: any): any
  {
    let obj = '{"fields":{';
    for (let rec = 0; rec < source.length; rec++)
    {
      obj += '"' + source[rec].id + '":{"name":"' + source[rec].name + '", "type":"' + source[rec].type + '"';
      if (source[rec].options.length > 0)
      {
        for (let i = 0; i < source[rec].options.length; i++)
        {
          if (i === 0)
          {
            obj += ',"options": [';
          }
          obj += '{"name":"' + source[rec].options[i].name + '","value":"' + source[rec].options[i].value + '"}';
          if ((i + 1) < source[rec].options.length)
          {
            obj += ',';
          }
        }
        obj += ']';
      }
      obj += '}';
      if ((rec + 1) < source.length)
      {
        obj += ',';
      }
    }
    obj += '}}';
    return JSON.parse(obj);
  }

  /**
   * General class names configuration
   */
  public classNames(): QueryBuilderClassNames
  {
    return {
      removeIcon: 'fa fa-times',
      addIcon: 'fa fa-plus',
      button: 'k-button',
      buttonGroup: 'd-flex',
      rightAlign: 'order-12 ml-auto',
      switchRow: 'd-flex',
      switchGroup: 'd-flex align-items-center',
      switchRadio: 'k-radio',
      switchLabel: 'k-radio-label',
      switchControl: '',
      row: 'row p-2 m-1 mr-2',
      rule: 'border',
      ruleSet: 'border',
      invalidRuleSet: 'alert alert-danger',
      operatorControl: 'form-control',
      operatorControlSize: 'col-auto px-0',
      fieldControl: 'form-control',
      fieldControlSize: 'col-auto',
      inputControl: 'form-control',
      inputControlSize: 'col-auto'
    };
  }

  /**
   * This method check if saved rules have their fields are still valid. None valid fields will be removed from rules.
   *
   * @param savedRules Query Builder saved rules
   * @param validFields Query Build valid fields being persisted
   */
  public cleanupRules(savedRules: any, validFields: any): any
  {
    const cleanRules = remove(savedRules, (n: any) =>
    {
      let found = false;
      forEach(validFields, (value, key) =>
      {
        if (key === n.field)
        {
          found = true;
        }
      });
      if (found)
      {
        return n;
      }
      else
      {
        return null;
      }
    });
    return cleanRules;
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        // Set the mainSections from localization
        this.translate.get('FIELD-MAIN-SECTIONS').subscribe({
          next: (res: string) =>
          {
            const splitted = res.split(',');
            for (let i = 0; i < splitted.length; i++)
            {
              if (this.mainSections.length > i)
              {
                if (splitted[i].includes('{{patientLabel}}'))
                {
                  this.mainSections[i].text = splitted[i].replace('{{patientLabel}}', sessionSetting.patientLabel);
                }
                else
                {
                  this.mainSections[i].text = splitted[i];
                }
              }
            }
          }
        });

        // Set the mainSections from localization
        this.translate.get('FIELD-SUB-SECTIONS').subscribe({
          next: (res: string) =>
          {
            const splitted = res.split(',');
            for (let i = 0; i < splitted.length; i++)
            {
              if (this.subSections.length > i)
              {
                if (splitted[i].includes('{{patientLabel}}'))
                {
                  this.subSections[i].text = splitted[i].replace('{{patientLabel}}', sessionSetting.patientLabel);
                }
                else
                {
                  this.subSections[i].text = splitted[i];
                }
              }
            }
          }
        });
      }
    });
  }
}
